import { useState, useEffect } from "react";
import Button from "../../../utils/components/Button";
import { FiTrash2 } from "react-icons/fi";
import { useUser } from "../../../utils/hooks/useUser";
import { getUnixTime } from "date-fns";
import { uniqueId } from "lodash";
import Modal from "../../../utils/components/Modal";
import { Translate } from "react-localize-redux";
import timeStampToDateFormat from "../../../utils/helpers/timestampToDateFormat";
import toBase64 from "../../../utils/helpers/toBase64";
import CustomDropzone from "../../../utils/components/CustomDropzone";

const Photos = ({ photos, type, disabled, onUpdate, location = [] }) => {
  const [files, setFiles] = useState(photos);
  const [isDisabled, setIsDisabled] = useState(true);
  const [openImageModal, setOpenImageModal] = useState(false);

  useEffect(() => {
    if (disabled !== isDisabled) setIsDisabled(disabled);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disabled]);

  const user = useUser();

  const handleSetFiles = async (acceptedFiles) => {
    const newFiles = [];
    for await (const file of acceptedFiles) {
      const base64 = await toBase64(file);
      const newFile = {
        url: URL.createObjectURL(file),
        base64,
        date: getUnixTime(new Date()),
        author: user?.name || "",
        authorId: user?.id || "",
        zone:
          typeof location === "string"
            ? location
            : location?.path || location?.map((item) => item.name).join(" > "),
        id: uniqueId("form-photo-"),
      };

      newFiles.push(newFile);
    }

    setFiles([...files, ...newFiles]);
  };

  const removePhoto = (id) => {
    const newFiles = files.filter((file) => file.id !== id);
    setFiles(newFiles);
  };

  useEffect(() => {
    const photosUrls = photos.map(p => p.url).sort().join(',');
    const filesUrls = files.filter(f => !f.base64).map(f => f.url).sort().join(',');
    
    if (photosUrls !== filesUrls || photos.length !== files.length) {
      setFiles(photos);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photos]);

  useEffect(() => {
    if (onUpdate) onUpdate(files);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files]);

  useEffect(() => {
    if (files.length === 1 && type === "single") {
      setIsDisabled(true);
    } else {
      if (!disabled) setIsDisabled(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files, onUpdate, type]);

  return (
    <>
      <div className="row w-100">
        <div className="col-12 col-lg-8 d-flex flex-column mt-4 mt-md-0">
          {files?.map((item, index) => (
            <div
              className="d-flex flex-column flex-md-row align-items-center mt-2"
              key={index}
            >
              <div className="col-6 col-md-4">
                <img
                  src={item.url}
                  alt={item.id}
                  className="mw-100 cursor--pointer"
                  onClick={() => setOpenImageModal(item.id)}
                />
              </div>

              <div className="text-secondary ms-0 ms-md-4 py-3">
                <p>{item.zone}</p>
                <p>
                  {item.author} - {timeStampToDateFormat(item.date)}
                </p>
                {!disabled && (
                  <Button
                    className="btn-sm btn-secondary text-uppercase px-4 py-2 mt-4"
                    onClick={() => removePhoto(item.id)}
                  >
                    <FiTrash2 className="fs-4" /> <Translate id="delete" />
                  </Button>
                )}
              </div>

              <Modal
                modalSize="m"
                show={openImageModal === item.id}
                setShow={() => setOpenImageModal(false)}
              >
                <div className="d-flex flex-column justify-content-center align-items-end">
                  <div className="d-flex justify-content-center align-items-center w-100">
                    <img
                      src={item.url}
                      alt={item.id}
                      className="mw-100 mh-100"
                    />
                  </div>
                  <div className="image-download-container mt-5 me-2 w-100 text-center">
                    <a
                      href={item.url}
                      target="_self"
                      rel="noopener noreferrer"
                      className="text-orange"
                    >
                      <Translate id="forms.click-for-download" />
                    </a>
                  </div>
                </div>
              </Modal>
            </div>
          ))}
        </div>
        <div className="col-11 col-lg-4">
          <div className="d-flex flex-column align-items-center justify-content-center border--dashed border-secondary border-3 rounded cursor--pointer p-3 mt-4">
            <div className="text-center">
              <CustomDropzone
                options={{
                  accept: {
                    "image/png": [],
                    "image/jpg": [],
                    "image/jpeg": [],
                    "image/jfif": [],
                  },
                  disabled: isDisabled,
                  multiple: type === "multiple",
                  onDrop: handleSetFiles,
                }}
                label={
                  <Translate
                    id={`forms.browse-files-to-upload-${
                      type === "single" ? `an-` : ``
                    }image`}
                  />
                }
                className="dropzone"
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
export default Photos;
